import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

/**
 * Creates an axios instance configured with interceptors to automatically
 * add the authentication token to all requests.
 *
 * @param getToken - Function that returns a promise with the access token
 * @returns Configured axios instance
 */
export default function createNestBackClient(getToken: () => Promise<string>): AxiosInstance {
  const baseURL = process.env.REACT_APP_REST_API_URL || "";

  const axiosInstance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      "req-sent-from": "techWebApp",
    },
  });

  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const token = await getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Error getting token:", error);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return axiosInstance;
}

export function useAxiosClient(): AxiosInstance {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const axiosClient = useMemo(() => {
    if (!isAuthenticated) {
      return createNestBackClient(async () => "");
    }
    return createNestBackClient(() => getAccessTokenSilently());
  }, [getAccessTokenSilently, isAuthenticated]);

  return axiosClient;
}
